import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import styled, { css } from "styled-components";
import { FormSubmit, Icon, OverlayTrigger, Select, NestedForm } from "components/common";
import { Button, Col, Label, Row } from "components/bootstrap";
import RuleBlockFormField from "components/rules/rule-builder/RuleBlockFormField";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import Errors from "./Errors";
import { RuleBuilderTypes } from "./types";
import RuleHelperTable from "../rule-helper/RulerHelperTable";
const BlockDescription = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.gray[50]};
  `
);
const SelectedBlock = styled.div(
  ({ theme }) => css`
    border-left: 1px solid ${theme.colors.gray["90"]};
    border-right: 1px solid ${theme.colors.gray["90"]};
    border-bottom: 1px solid ${theme.colors.gray["90"]};
    border-radius: 0 0 10px 10px;
    padding: ${theme.spacings.md};
  `
);
const SelectedBlockInfo = styled(Row)(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.md};
  `
);
const OptionTitle = styled.p(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.xxs};
  `
);
const SelectRow = styled(Row)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.xxs};
    margin-bottom: ${theme.spacings.xxs};
  `
);
const OptionDescription = styled.p(
  ({ theme, $isSelected }) => css`
    color: ${$isSelected ? theme.colors.gray[90] : theme.colors.gray[50]};
    margin-bottom: ${theme.spacings.xxs};
    font-size: 0.75rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `
);
const OptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
const DeprecatedLabel = styled.span`
  float: right;
`;
const RuleBlockForm = ({
  existingBlock = void 0,
  onAdd,
  onCancel,
  onSelect,
  onUpdate,
  options,
  order,
  outputVariableList = [],
  selectedBlockDict = void 0,
  type
}) => {
  const [initialValues, setInitialValues] = useState({});
  const sendTelemetry = useSendTelemetry("pipeline-rule-builder");
  useEffect(() => {
    const newInitialValues = {};
    if (selectedBlockDict) {
      selectedBlockDict.params.forEach((param) => {
        const initialBlockValue = (existingBlock == null ? void 0 : existingBlock.function) === selectedBlockDict.name ? existingBlock == null ? void 0 : existingBlock.params[param.name] : void 0;
        if (initialBlockValue || param.type === RuleBuilderTypes.Boolean && typeof initialBlockValue === "boolean") {
          newInitialValues[param.name] = initialBlockValue;
        } else {
          newInitialValues[param.name] = param.default_value;
        }
      });
    }
    setInitialValues(newInitialValues);
  }, [selectedBlockDict, existingBlock]);
  const handleChange = (option, resetForm) => {
    sendTelemetry(
      type === "condition" ? TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.NEW_CONDITION_SELECTED : TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.NEW_ACTION_SELECTED,
      {
        app_action_value: `select-${type}`,
        event_details: { option }
      }
    );
    resetForm();
    onSelect(option);
  };
  const resetField = (fieldName, setFieldValue) => {
    setFieldValue(fieldName, null);
  };
  const onSubmit = (values) => {
    if (existingBlock) {
      sendTelemetry(
        type === "condition" ? TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.UPDATE_CONDITION_CLICKED : TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.UPDATE_ACTION_CLICKED,
        {
          app_action_value: `update-${type}-button`
        }
      );
      onUpdate(values, selectedBlockDict == null ? void 0 : selectedBlockDict.name);
    } else {
      sendTelemetry(
        type === "condition" ? TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.ADD_CONDITION_CLICKED : TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.ADD_ACTION_CLICKED,
        {
          app_action_value: `add-${type}-button`
        }
      );
      onAdd(values);
    }
  };
  const optionRenderer = (option, isSelected) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(OptionContainer, null, /* @__PURE__ */ React.createElement(OptionTitle, null, option.label), /* @__PURE__ */ React.createElement("span", null, option.deprecated && /* @__PURE__ */ React.createElement(Label, { bsStyle: "warning", bsSize: "xs" }, "Deprecated"))), option.description && /* @__PURE__ */ React.createElement(OptionDescription, { $isSelected: isSelected }, option.description));
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(Formik, { enableReinitialize: true, onSubmit, initialValues }, ({ resetForm, setFieldValue, isValid }) => /* @__PURE__ */ React.createElement(NestedForm, null, /* @__PURE__ */ React.createElement(SelectRow, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    Select,
    {
      id: `existingBlock-select-${type}`,
      name: `existingBlock-select-${type}`,
      placeholder: `Add ${type}`,
      options,
      optionRenderer,
      clearable: false,
      autoFocus: true,
      onChange: (option) => handleChange(option, resetForm),
      value: (selectedBlockDict == null ? void 0 : selectedBlockDict.name) || ""
    }
  ))), selectedBlockDict && /* @__PURE__ */ React.createElement(SelectedBlock, null, /* @__PURE__ */ React.createElement(SelectedBlockInfo, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement("h5", null, (existingBlock == null ? void 0 : existingBlock.step_title) || selectedBlockDict.rule_builder_name, /* @__PURE__ */ React.createElement(
    OverlayTrigger,
    {
      trigger: "click",
      rootClose: true,
      placement: "right",
      title: "Function Syntax Help",
      width: 700,
      overlay: /* @__PURE__ */ React.createElement(
        RuleHelperTable,
        {
          entries: [selectedBlockDict],
          expanded: { [selectedBlockDict.name]: true }
        }
      )
    },
    /* @__PURE__ */ React.createElement(Button, { bsStyle: "link" }, /* @__PURE__ */ React.createElement(Icon, { name: "help", title: "Function Syntax Help", "data-testid": "funcSyntaxHelpIcon" }))
  ), selectedBlockDict.deprecated && /* @__PURE__ */ React.createElement(DeprecatedLabel, null, "\xA0", /* @__PURE__ */ React.createElement(Label, { bsStyle: "warning", bsSize: "xs" }, "Deprecated"))), /* @__PURE__ */ React.createElement(BlockDescription, null, selectedBlockDict.description))), selectedBlockDict.params.map((param) => /* @__PURE__ */ React.createElement(Row, { key: `${order}_${param.name}` }, /* @__PURE__ */ React.createElement(
    RuleBlockFormField,
    {
      param,
      functionName: selectedBlockDict.name,
      order,
      blockId: existingBlock == null ? void 0 : existingBlock.id,
      outputVariableList,
      blockType: type,
      resetField: (fieldName) => resetField(fieldName, setFieldValue)
    }
  ))), /* @__PURE__ */ React.createElement(Errors, { objectWithErrors: existingBlock }), /* @__PURE__ */ React.createElement(
    FormSubmit,
    {
      bsSize: "small",
      disabledSubmit: !isValid,
      submitButtonText: existingBlock ? "Update" : "Add",
      submitButtonType: "submit",
      onCancel: () => {
        resetForm();
        onCancel();
      }
    }
  ))))));
};
export default RuleBlockForm;
